import React from 'react';
import {
  DomainAdd as DomainAddIcon,
  CloudUpload as CloudUploadIcon,
  Dns as DnsIcon,
  IntegrationInstructions as XmlIcon,
  TableChart as SheetIcon,
  RuleFolder as VerdictIcon,
} from '@mui/icons-material';

import DepartmentLayout from '../Layout/DepartmentLayout';
import Facilities from './Facilities';
import NhisImports from './NhisImports';
import NhisCatalogueImport from './NhisCatalogueImport';
import ClaimsImportPanel from './ClaimsImportPanel';
import NhiaReportPanel from './NhiaReportPanel';

// Tokens first — Admin.css and friends consume variables this declares.
import './ict-tokens.css';
import './Admin.css';
import './Facilities.css';
import './Modal.css';
import './notify.css';

// ============================================================================
//  IT / ICT department
// ============================================================================
//
//  Ported from the standalone Gigatt project's Admin Panel, where Facility
//  Onboarding was the first item under Administrative and NHIS Imports sat
//  further down the same list. Both are technical-operations work rather than
//  clinical or administrative, which is why they live here.
//
//  Facility Onboarding registers the CLIENT facilities this installation does
//  claims work for. That is a different thing from `assembly`, the single row
//  describing the facility that owns this installation.
//
//  The NHIS Catalogue group holds two importers for two different sources —
//  Tariff Sheets (.xlsx → insured_services) and Booklet PDFs (→ the reference
//  catalogue). Both preview before they write, and both commit in one
//  transaction. See MENU_ITEMS for which is which.
//
//  Department id 27 matches 'IT / ICT Department' in Components/data/department.js,
//  so DepartmentLayout's component-permission gating works the same way it does
//  for every other department.

// Each section is wrapped in .ict-theme — that div is where the 31 CSS
// variables the ported stylesheets depend on are declared.
const scoped = (Component) => function Scoped(props) {
  return (
    <div className="ict-theme">
      <Component {...props} />
    </div>
  );
};

const MENU_ITEMS = [
  {
    key: 'FacilityOnboarding',
    label: 'Facility Onboarding',
    section: 'Onboarding',
    icon: <DomainAddIcon />,
    component: scoped(Facilities),
  },
  // The two catalogue importers read DIFFERENT sources into DIFFERENT tables and
  // neither replaces the other:
  //
  //   Tariff Sheets  the .xlsx a facility is handed → insured_services, the
  //                  table billing and claims actually charge from. Needs
  //                  nothing but Node; the browser reads the spreadsheet.
  //
  //   Booklet PDFs   the Annex-C manual, medicines list and level tariff →
  //                  the reference catalogue (gdrgs, medicines, facility
  //                  levels). Needs python3 + pypdf on the API host.
  //
  // Sheets first because that is the one an operator runs when prices change.
  {
    key: 'NhisCatalogue',
    label: 'Tariff Sheets',
    section: 'NHIS Catalogue',
    icon: <SheetIcon />,
    // Not scoped(): this screen is built on the brand tokens and MUI rather
    // than the ported Admin.css, so it needs none of the .ict-theme variables.
    component: NhisCatalogueImport,
  },
  {
    key: 'NhisImports',
    label: 'Booklet PDFs',
    section: 'NHIS Catalogue',
    icon: <CloudUploadIcon />,
    component: scoped(NhisImports),
  },
  // Sits after the catalogue on purpose: the tariffs have to be loaded before
  // an imported claim's G-DRG and tariff figures mean anything.
  {
    key: 'ClaimsImport',
    label: 'Claims XML Import',
    section: 'Claims',
    icon: <XmlIcon />,
    component: scoped(ClaimsImportPanel),
  },
  //  Immediately after the XML import, because it is the other end of the same
  //  round trip: that screen sends claims out, this one reads back what NHIA said
  //  about them. Not scoped() — like NhisCatalogueImport it is built on the brand
  //  tokens rather than the ported Admin.css, so it needs no .ict-theme variables.
  {
    key: 'NhiaReport',
    label: 'NHIA Import Report',
    section: 'Claims',
    icon: <VerdictIcon />,
    component: NhiaReportPanel,
  },
];

export default function ICT() {
  return (
    <DepartmentLayout
      departmentId={27}
      departmentName="IT / ICT"
      departmentIcon={<DnsIcon />}
      menuItems={MENU_ITEMS}
    />
  );
}
